import React from 'react';
import { ArrowLeft, Mail, MapPin, Phone, Clock, Users, Video, Shield } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';

const VODAbout = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>About Us | LTV VOD - Video Streaming Platform</title>
        <meta name="description" content="Learn about LTV VOD, your premium video-on-demand streaming platform. Discover our mission, team, and commitment to delivering exceptional entertainment content." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/about" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          <Link to="/vod" className="flex items-center gap-2">
            <img src="/ltv-icon.png" alt="LTV VOD" className="h-8 w-8 brightness-0 invert" />
            <span className="font-bold">LTV VOD</span>
          </Link>
        </div>
      </header>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-purple-900/20 to-transparent py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About LTV VOD</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Your premier destination for exclusive video content, live streaming events, and creator-driven entertainment.
          </p>
        </div>
      </div>
      
      {/* Content */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-12">
          {/* Mission Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-300 mb-4">
                  LTV VOD is a video-on-demand platform operated by LTV Production LTD, dedicated to bringing you premium entertainment content from talented creators around the world. We exist because we believe that every community, every culture, and every creative voice deserves a platform that honours their work and delivers it to audiences who value authenticity over algorithms.
                </p>
                <p className="text-gray-300 mb-4">
                  We believe in empowering content creators while delivering an exceptional viewing experience to our audience. Our platform bridges the gap between creators and viewers, offering a seamless way to discover, watch, and support the content you love. Whether you are passionate about Caribbean culture, independent film, live music, or documentary storytelling, LTV VOD provides a home for content that matters.
                </p>
                <p className="text-gray-300 mb-4">
                  From exclusive shows and live events to engaging short-form content, LTV VOD is your all-in-one streaming destination. We are more than a platform — we are a community of creators, viewers, and storytellers who believe that the best entertainment comes from real people with real stories to tell.
                </p>
                <p className="text-gray-300">
                  Our commitment extends beyond just hosting videos. We invest in our creators through fair revenue sharing, production support, marketing guidance, and community-building tools that help them turn their passion into a sustainable career. When our creators succeed, our entire community benefits from better, more diverse, and more meaningful content.
                </p>
              </div>
              <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-2xl p-8 border border-purple-500/20">
                <h3 className="text-xl font-semibold mb-4">What We Offer</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Video className="h-5 w-5 text-purple-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Exclusive video content and series</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-purple-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Creator channels and subscriptions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-purple-400 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Premium ad-free viewing experience</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
          
          {/* Company Info Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Our Company</h2>
            <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">LTV Production LTD</h3>
                  <p className="text-gray-300 mb-4">
                    LTV Production LTD is a UK-based media and entertainment company specializing in video production, live streaming, and digital content distribution. Established with the vision of creating a world-class entertainment platform that celebrates diverse cultures and amplifies underrepresented voices, LTV Production has grown from a small production house into a comprehensive media company serving audiences globally.
                  </p>
                  <p className="text-gray-300 mb-4">
                    Founded with a passion for storytelling and technology, we combine creative excellence with cutting-edge streaming infrastructure to deliver the best possible viewing experience. Our team brings together expertise in film production, software engineering, digital marketing, and community management to create a platform that serves both creators and viewers at the highest level.
                  </p>
                  <p className="text-gray-300">
                    From our headquarters in London, we work with creators and production teams across the United Kingdom, Caribbean, Europe, and beyond. Our state-of-the-art streaming infrastructure, powered by enterprise-grade content delivery networks, ensures that our content reaches viewers worldwide with exceptional quality and reliability, whether they are watching from a mobile device on the go or a home cinema setup.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Headquarters</h4>
                      <p className="text-gray-400">London, United Kingdom</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Contact</h4>
                      <p className="text-gray-400">info@ltvproduction.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Support Hours</h4>
                      <p className="text-gray-400">Monday - Friday, 9AM - 6PM GMT</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          {/* Values Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Creator First</h3>
                <p className="text-gray-400 text-sm">
                  We prioritize our creators, providing them with the tools and platform to succeed and connect with their audience.
                </p>
              </div>
              <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Quality & Trust</h3>
                <p className="text-gray-400 text-sm">
                  We maintain the highest standards for content quality and viewer trust, ensuring a safe and enjoyable experience.
                </p>
              </div>
              <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-4">
                  <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Innovation</h3>
                <p className="text-gray-400 text-sm">
                  We continuously improve our platform with cutting-edge technology to deliver the best streaming experience.
                </p>
              </div>
            </div>
          </section>
          
          {/* Content Guidelines Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Content Standards</h2>
            <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8">
              <p className="text-gray-300 mb-4">
                LTV VOD is committed to providing a safe and respectful environment for all users. Our content standards ensure:
              </p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  All content complies with applicable laws and regulations
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  Age-appropriate content labeling and restrictions
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  Zero tolerance for harmful, hateful, or illegal content
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  Respect for intellectual property and copyright
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-400">•</span>
                  Community guidelines enforcement
                </li>
              </ul>
              <p className="mt-4 text-gray-400">
                For more details, see our <Link to="/vod/content-policy" className="text-purple-400 hover:underline">Content Policy</Link> and <Link to="/vod/dmca" className="text-purple-400 hover:underline">DMCA Policy</Link>.
              </p>
            </div>
          </section>
          
          {/* Our Story Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="prose prose-invert max-w-none">
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  LTV Production was born from a simple observation: the entertainment industry was not serving every community equally. While major streaming platforms invested billions in content for mainstream audiences, vibrant creative communities — particularly those rooted in Caribbean, African diaspora, and independent cultural traditions — were being underserved and undervalued. We set out to change that.
                </p>
                <p>
                  What started as a live event production company has evolved into a full-service digital entertainment platform. Our journey from filming local events in London to building a global streaming platform has taught us invaluable lessons about what audiences truly want: authentic content created by people who understand and respect their culture, delivered through technology that just works.
                </p>
                <p>
                  Every decision we make — from the creators we partner with to the features we build — is guided by our belief that entertainment should celebrate diversity, empower creators, and bring communities together. We are not trying to compete with the major streaming giants. Instead, we are building something different: a platform where culture comes first, where creators are partners rather than products, and where every viewer feels seen and valued.
                </p>
                <p>
                  As we continue to grow, our commitment to these principles only strengthens. We are expanding our content library, investing in original productions, developing new features for creators and viewers, and building partnerships that extend our reach while staying true to our mission. The future of LTV Production is bright, and we are excited to share it with our growing community of creators, viewers, and supporters around the world.
                </p>
              </div>
            </div>
          </section>
          
          {/* CTA Section */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-2xl p-8 border border-purple-500/20">
              <h2 className="text-2xl font-bold mb-4">Ready to Start Watching?</h2>
              <p className="text-gray-300 mb-6">
                Join thousands of viewers enjoying exclusive content on LTV VOD.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <Link 
                  to="/vod/browse"
                  className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-full font-semibold transition-colors"
                >
                  Browse Content
                </Link>
                <Link 
                  to="/vod/membership"
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full font-semibold transition-colors"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default VODAbout;
